/**
 * ai-photo-style embed: "Maak een aquarel van je foto" op een KC-PDP.
 * Wordt als IIFE geladen (Shop Asset of dev-adapter) en draait volledig in Shadow DOM.
 */
import { render } from 'preact';
import css from './ui/styles.css?inline';
import { log, readConfig, setDebug } from './config';
import {
  applyUploaded,
  fetchDesign,
  findPhotoFrame,
  keepPreviewInSync,
  lastResult,
  readCoid,
  reloadLightEditorOnOpen,
} from './kc';
import { Flow } from './flow';
import { track } from './analytics';
import { Cta } from './ui/Cta';
import { ModalRoot } from './ui/Modal';
import { ApiClient } from './api';
import { loadCatalog, originScope, resolveCatalog } from './catalog';
import { applyShopTheme } from './theme';
import { errorCopy, isQuietlyUnavailable, toEmbedError } from './errors';

// Alleen tijdens synchrone uitvoering beschikbaar, dus direct vastleggen.
const currentScript = document.currentScript as HTMLScriptElement | null;

function shadowMount(host: HTMLElement, shopId?: string): HTMLElement {
  applyShopTheme(host, shopId);
  const root = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = css;
  root.appendChild(style);
  const mount = document.createElement('div');
  root.appendChild(mount);
  return mount;
}

interface Anchor {
  parent: Element;
  before: Element | null;
  /** Bootstrap-class van de pagina zelf, zodat maar één knop tegelijk zichtbaar is. */
  className?: string;
}

/**
 * Mobiel: direct onder de kaartpreview, als eerste element van het productpaneel. (De mobiele
 * "Bewerken"-knop zit in een vaste balk onderaan; daar zou de knop een kwart van het scherm afdekken.)
 * Desktop: boven de "Bewerken"-knop in de rechterkolom.
 */
function ctaAnchors(): Anchor[] {
  const anchors: Anchor[] = [];
  const panel = document.querySelector('.pdp-panel');
  if (panel) anchors.push({ parent: panel, before: panel.firstElementChild, className: 'd-lg-none' });
  const desktopForm = document.querySelector('.choose-card-container:not(.choose-card-container-b) form.choose-card-form');
  if (desktopForm?.parentElement) anchors.push({ parent: desktopForm.parentElement, before: desktopForm });
  if (anchors.length) return anchors;
  const builder = document.querySelector('.product-builder-container');
  return builder?.parentElement ? [{ parent: builder.parentElement, before: builder }] : [];
}

/** Turnstile in de gewone DOM, boven de modal: blijft zichtbaar als Cloudflare toch een klik vraagt. */
function turnstileContainer(): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('data-ai-photo-style-turnstile', '');
  Object.assign(el.style, {
    position: 'fixed',
    left: '50%',
    bottom: 'calc(110px + env(safe-area-inset-bottom))',
    transform: 'translateX(-50%)',
    zIndex: '2147483001',
    maxWidth: 'calc(100vw - 32px)',
  });
  document.body.appendChild(el);
  return el;
}

async function boot(): Promise<void> {
  let cfg = readConfig(currentScript);
  if (!cfg) return;
  setDebug(cfg.debug);
  if (document.body.id !== 'pdp') return log('geen PDP, niets te doen');
  if (document.querySelector('[data-ai-photo-style]')) return log('al geladen');
  const coid = readCoid();
  if (!coid) return log('geen coid gevonden');
  try {
    const design = await fetchDesign(coid);
    if (!findPhotoFrame(design)) return log('ontwerp heeft geen fotokader, embed niet actief');
  } catch {
    return log('ontwerp niet op te halen, embed niet actief');
  }

  const shopId = () => cfg.catalog?.shopId ?? originScope(location.origin)?.shopId;
  try {
    cfg = await loadCatalog(cfg, new ApiClient(cfg.apiBase, undefined, undefined, undefined, undefined, cfg.configId));
  } catch (err) {
    const code = toEmbedError(err).code;
    // Uit of niet gepubliceerd hoort eruit te zien alsof de tool er niet is.
    if (isQuietlyUnavailable(code)) return log('configuratie uit, klant ziet niets', code);
    const copy = errorCopy(code, undefined, shopId());
    for (const { parent, before, className } of ctaAnchors()) {
      const host = document.createElement('div');
      host.setAttribute('data-ai-photo-style', 'unavailable');
      if (className) host.className = className;
      parent.insertBefore(host, before);
      render(<div class="err" role="alert"><h3>{copy.title}</h3><p>{copy.message}</p></div>, shadowMount(host, shopId()));
    }
    return log('catalogus niet beschikbaar', code);
  }
  if (cfg.catalog && cfg.ctaImageUrl && !cfg.catalog.ctaImageUrl) {
    cfg = { ...cfg, catalog: { ...cfg.catalog, ctaImageUrl: cfg.ctaImageUrl } };
  }
  if (isQuietlyUnavailable(resolveCatalog(cfg).error)) return log('configuratie uit, klant ziet niets');

  const flow = new Flow(cfg, coid);
  flow.turnstileContainer = turnstileContainer();

  const modalHost = document.createElement('div');
  modalHost.setAttribute('data-ai-photo-style', 'modal');
  document.body.appendChild(modalHost);
  render(<ModalRoot flow={flow} />, shadowMount(modalHost, shopId()));

  const anchors = ctaAnchors();
  for (const { parent, before, className } of anchors) {
    const host = document.createElement('div');
    host.setAttribute('data-ai-photo-style', 'cta');
    if (className) host.className = className;
    parent.insertBefore(host, before);
    render(<Cta flow={flow} />, shadowMount(host, shopId()));
  }
  if (!anchors.length) log('geen plek voor de knop gevonden');

  // Na herladen de aquarel van eerder in deze sessie terugzetten op de nieuwe order (geen nieuwe generatie).
  const last = lastResult(flow.scope);
  if (last && !flow.state.applied) {
    try {
      await applyUploaded(coid, last, flow.scope);
      flow.markRestored();
      track('apply_success', 'restored');
      log('aquarel teruggezet na herladen');
    } catch {
      log('terugzetten na herladen mislukt');
    }
  }

  // Support/test: #aps_reuse=<prediction-id> haalt een eerder gemaakte aquarel terug en opent de modal.
  // Ook bij een hash-wijziging op een al geladen pagina (dan herlaadt de browser niet).
  const reuseFromUrl = async () => {
    const reuseId =
      new URLSearchParams(location.hash.slice(1)).get('aps_reuse') ??
      new URLSearchParams(location.search).get('aps_reuse');
    if (reuseId && (await flow.importPrediction(reuseId))) {
      log('eerdere aquarel teruggehaald', reuseId);
      flow.open();
    }
  };
  window.addEventListener('hashchange', () => void reuseFromUrl());
  await reuseFromUrl();

  keepPreviewInSync(() => flow.appliedCoid);
  reloadLightEditorOnOpen();
  track('cta_view');
  log('actief', { coid, apiBase: cfg.apiBase, mock: cfg.mockScenario ?? null, model: cfg.modelOverride ?? null });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => void boot());
else void boot();
